import React, { useEffect } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Sun, 
  SunMedium, 
  Moon, 
  Clock, 
  Pill, 
  Syringe, 
  Flame, 
  Sparkles,
  CalendarDays,
  FileText
} from 'lucide-react';
import { TIME_SLOTS, PRN_SLOT, formatFullDate, formatDateIso } from '../../utils/calendarUtils';
import { normalizeCompoundCategory, formatDosageRange } from '../../utils/sortUtils';

export default function DayOverviewModal({
  isOpen,
  onClose,
  selectedDate,
  onNavigateDate,
  schedule
}) {
  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && onNavigateDate) {
        onNavigateDate(-1);
      } else if (e.key === 'ArrowRight' && onNavigateDate) {
        onNavigateDate(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNavigateDate]);

  if (!isOpen || !selectedDate) return null;

  const todayStr = formatDateIso(new Date());
  const selectedDateStr = formatDateIso(selectedDate);
  const isToday = selectedDateStr === todayStr;

  const getCategoryTheme = (category) => {
    const cat = normalizeCompoundCategory(category);
    switch (cat) {
      case 'Peptide':
        return {
          badge: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
          dot: 'bg-purple-400',
          icon: Syringe
        };
      case 'Hormones & AAS':
        return {
          badge: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
          dot: 'bg-rose-400',
          icon: Flame
        };
      case 'Supplement':
      default:
        return {
          badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
          dot: 'bg-sky-400',
          icon: Pill
        };
    }
  };

  const getSlotIcon = (slotId) => {
    switch (slotId) {
      case 'AM': return Sun;
      case 'NOON': return SunMedium;
      case 'BEDTIME': return Moon;
      case 'PRN': return Clock;
      default: return Clock;
    }
  };

  const totalDoses = schedule ? (
    (schedule.bySlot?.AM?.length || 0) +
    (schedule.bySlot?.NOON?.length || 0) +
    (schedule.bySlot?.BEDTIME?.length || 0)
  ) : 0;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-slate-800 flex items-center justify-between gap-4 bg-slate-900/90 sticky top-0 z-10">
          <div className="flex items-center space-x-3 min-w-0">
            <div className={`p-2.5 rounded-2xl border ${isToday ? 'bg-sky-500/20 text-sky-400 border-sky-500/40' : 'bg-slate-800/80 text-slate-300 border-slate-700/60'}`}>
              <CalendarDays className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <h2 className="text-base sm:text-lg font-bold text-white truncate">
                  {formatFullDate(selectedDate)}
                </h2>
                {isToday && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30">
                    Today
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {totalDoses} administration {totalDoses === 1 ? 'event' : 'events'} across the day
              </p>
            </div>
          </div>

          {/* Quick Date Stepper & Close */}
          <div className="flex items-center space-x-2 shrink-0">
            {onNavigateDate && (
              <div className="flex items-center space-x-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
                <button
                  type="button"
                  onClick={() => onNavigateDate(-1)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
                  title="Previous Day (Left Arrow)"
                  aria-label="Previous Day"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onNavigateDate(1)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
                  title="Next Day (Right Arrow)"
                  aria-label="Next Day"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800 transition"
              title="Close (Esc)"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-6">
          {TIME_SLOTS.map((slot) => {
            const SlotIcon = getSlotIcon(slot.id);
            const protocols = schedule?.bySlot?.[slot.id] || [];

            return (
              <section key={slot.id} className="space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-slate-800/80">
                  <div className="flex items-center space-x-2">
                    <span className={`p-1.5 rounded-lg ${slot.bgColor} ${slot.textColor} border ${slot.borderColor}`}>
                      <SlotIcon className="w-4 h-4" />
                    </span>
                    <h3 className="text-sm font-bold text-slate-200">
                      {slot.label}
                    </h3>
                    <span className="text-[11px] text-slate-500 font-medium">
                      ({slot.timeRange})
                    </span>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700/60">
                    {protocols.length} {protocols.length === 1 ? 'compound' : 'compounds'}
                  </span>
                </div>

                {protocols.length === 0 ? (
                  <div className="p-4 rounded-2xl bg-slate-950/40 border border-dashed border-slate-800/80 text-center text-xs text-slate-500">
                    No compounds scheduled for {slot.shortLabel.toLowerCase()} on this date.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {protocols.map((protocol, idx) => {
                      const theme = getCategoryTheme(protocol.category);
                      const CategoryIcon = theme.icon;
                      const dosageDisplay = formatDosageRange(protocol);

                      return (
                        <div
                          key={`${protocol.id || protocol.name}-${idx}`}
                          className="p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800/90 hover:border-slate-700 transition flex flex-col justify-between space-y-2.5"
                        >
                          <div>
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-bold text-sm text-white leading-snug">
                                {protocol.name}
                              </h4>
                              <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0 ${theme.badge}`}>
                                <CategoryIcon className="w-3 h-3" />
                                <span>{normalizeCompoundCategory(protocol.category)}</span>
                              </span>
                            </div>

                            <div className="flex items-center flex-wrap gap-2 mt-2">
                              <span className="px-2 py-0.5 rounded-lg text-xs font-bold bg-slate-800 text-sky-300 border border-slate-700/80">
                                {dosageDisplay}
                              </span>
                              {protocol.administrationFormat && (
                                <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-slate-900 text-slate-300 border border-slate-800">
                                  {protocol.administrationFormat}
                                </span>
                              )}
                              {protocol.frequencySchedule && (
                                <span className="text-[11px] text-slate-400 font-mono">
                                  • {protocol.frequencySchedule}
                                </span>
                              )}
                            </div>
                          </div>

                          {(protocol.intendedOutcome || protocol.notes) && (
                            <div className="pt-2 border-t border-slate-800/60 text-xs text-slate-400 space-y-1">
                              {protocol.intendedOutcome && (
                                <p className="line-clamp-2">
                                  <strong className="text-slate-300">Goal:</strong> {protocol.intendedOutcome}
                                </p>
                              )}
                              {protocol.notes && (
                                <p className="line-clamp-2 text-slate-500 italic">
                                  <strong className="not-italic text-slate-400">Note:</strong> {protocol.notes}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}

          {/* As-Needed / PRN Section if any exist */}
          {schedule?.bySlot?.PRN?.length > 0 && (
            <section className="space-y-3 pt-4 border-t border-slate-800">
              <div className="flex items-center justify-between pb-2 border-b border-slate-800/80">
                <div className="flex items-center space-x-2">
                  <span className={`p-1.5 rounded-lg ${PRN_SLOT.bgColor} ${PRN_SLOT.textColor} border ${PRN_SLOT.borderColor}`}>
                    <Clock className="w-4 h-4" />
                  </span>
                  <h3 className="text-sm font-bold text-slate-200">
                    {PRN_SLOT.label}
                  </h3>
                  <span className="text-[11px] text-slate-500 font-medium">
                    (Acute / Flexible Stack Items)
                  </span>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700/60">
                  {schedule.bySlot.PRN.length} items
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {schedule.bySlot.PRN.map((protocol, idx) => {
                  const theme = getCategoryTheme(protocol.category);
                  const CategoryIcon = theme.icon;
                  const dosageDisplay = formatDosageRange(protocol);

                  return (
                    <div
                      key={`prn-${protocol.id || protocol.name}-${idx}`}
                      className="p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800/90 flex flex-col justify-between space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-bold text-sm text-white">{protocol.name}</h4>
                        <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${theme.badge}`}>
                          <CategoryIcon className="w-3 h-3" />
                          <span>{normalizeCompoundCategory(protocol.category)}</span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs">
                        <span className="font-bold text-emerald-400">{dosageDisplay}</span>
                        <span className="text-slate-500">•</span>
                        <span className="text-slate-400">{protocol.timingOfDay || 'PRN'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-950/50 flex items-center justify-between text-xs text-slate-500">
          <div>
            Tip: Use <kbd className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[10px]">←</kbd> and <kbd className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[10px]">→</kbd> arrow keys to switch days, <kbd className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[10px]">Esc</kbd> to close
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
